/**
 * OutreachRecord — what an outreach opens into instead of a deal room.
 *
 * Daniel's rule: "the outreach deals from the past and moving forward should
 * not have deal pages. It should just be treated like outreach."
 *
 * An unsolicited offer is a hail mary sent at volume. Opening one into the
 * full deal room presents thesis, red flags, comp set and IRR fields that are
 * almost always blank, which teaches everyone that blank fields are normal —
 * and that habit is expensive on the deals where they are not.
 *
 * So this shows what an outreach actually has: the property and its physicals,
 * who was contacted, what was offered and when, what came back, and the log.
 * Nothing that implies an underwriting exists.
 *
 * The promote step is the moment that changes. When an owner engages, the
 * record becomes a deal, gets a folder, and enters the extract → normalise →
 * underwrite → load pass. That is the point at which there is something to put
 * on a deal page, and not before.
 */

import { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { fmt } from "../../utils/formatters";

const money = (v) => (Number.isFinite(Number(v)) && Number(v) ? fmt.currencyOrDash(v) : "—");
const sf0 = (v) => (Number.isFinite(Number(v)) && Number(v) ? Number(v).toLocaleString() : "—");

const CARD = "bg-[#242424] border border-[#3a3a3a] rounded-lg";
const LABEL = "text-[10px] font-semibold uppercase tracking-wider text-[#8a8a8a]";

function Fact({ label, children }) {
  return (
    <div className="px-4 py-2.5">
      <div className={LABEL}>{label}</div>
      <div className="text-[13px] text-[#f4f4f4] mt-0.5">{children ?? "—"}</div>
    </div>
  );
}

export default function OutreachRecord({ deal, onBack, onPromote, readOnly = false }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const addresses = (() => {
    const v = deal?.addresses ?? deal?.address;
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim().startsWith("[")) {
      try { return JSON.parse(v); } catch { return [v]; }
    }
    return v ? [v] : [];
  })();

  useEffect(() => {
    let live = true;
    const addr = addresses[0];
    if (!addr) { setLoading(false); return; }
    supabase
      .from("outreach_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (!live) return;
        if (error) console.error("outreach log:", error.message);
        // The log keys on the property, not the deal, so match on address.
        const mine = (data || []).filter((r) => {
          const a = String(r.property_address || r.address || "").toLowerCase();
          return a && addresses.some((x) => a.includes(String(x).toLowerCase().slice(0, 18)));
        });
        setLog(mine);
        setLoading(false);
      });
    return () => { live = false; };
  }, [deal?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const latest = log[0];

  return (
    <div className="max-w-4xl mx-auto w-full px-6 py-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="text-[13px] text-[#8a8a8a] hover:text-[#f4f4f4]">
          ← Back
        </button>
        <span className="px-2 py-1 rounded-lg bg-[#2c2c2c] text-[#b4b4b4] text-[11px]">
          Outreach record
        </span>
      </div>

      <div className={CARD}>
        <div className="px-4 py-3 border-b border-[#3a3a3a]">
          <h2 className="text-[15px] font-semibold text-[#f4f4f4]">
            {addresses[0] || "Untitled outreach"}
          </h2>
          {addresses.length > 1 && (
            <p className="text-[11px] text-[#8a8a8a] mt-0.5">
              and {addresses.length - 1} more address{addresses.length === 2 ? "" : "es"}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#303030]">
          <Fact label="Market">{deal?.market}</Fact>
          <Fact label="Building area">{sf0(deal?.squareFeet ?? deal?.square_footage)} SF</Fact>
          <Fact label="Year built">{deal?.yearBuilt ?? deal?.year_built}</Fact>
          <Fact label="Stage">{deal?.stage ?? deal?.deal_stage}</Fact>
        </div>
      </div>

      <div className={CARD}>
        <div className="px-4 py-3 border-b border-[#3a3a3a]">
          <h3 className="text-[13px] font-semibold text-[#f4f4f4]">What was offered</h3>
        </div>
        {loading ? (
          <p className="px-4 py-5 text-[12px] text-[#8a8a8a]">Loading the outreach log…</p>
        ) : !latest ? (
          <p className="px-4 py-5 text-[12px] text-[#8a8a8a]">
            Nothing has been sent on this property yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#303030]">
            <Fact label="Offer">
              {latest.offer_psf ? `$${Number(latest.offer_psf).toFixed(2)}/SF` : "—"}
            </Fact>
            <Fact label="Total">{money(latest.offer_total)}</Fact>
            <Fact label="Sent">{String(latest.created_at || "").slice(0, 10)}</Fact>
            <Fact label="Response">{latest.response || latest.status || "none yet"}</Fact>
          </div>
        )}
      </div>

      <div className={CARD}>
        <div className="px-4 py-3 border-b border-[#3a3a3a] flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-[#f4f4f4]">Outreach log</h3>
          <span className="text-[11px] text-[#8a8a8a]">{log.length} entr{log.length === 1 ? "y" : "ies"}</span>
        </div>
        {!log.length ? (
          <p className="px-4 py-5 text-[12px] text-[#8a8a8a]">No entries.</p>
        ) : (
          <div className="divide-y divide-[#303030] max-h-72 overflow-y-auto">
            {log.map((r) => (
              <div key={r.id} className="px-4 py-2.5 flex items-baseline gap-3">
                <span className="text-[11px] text-[#8a8a8a] w-20 shrink-0">
                  {String(r.created_at || "").slice(0, 10)}
                </span>
                <span className="text-[12.5px] text-[#b4b4b4] flex-1 min-w-0 truncate">
                  {r.subject || r.note || r.channel || "—"}
                </span>
                {r.offer_psf && (
                  <span className="text-[12px] tabular-nums text-[#8a8a8a]">
                    ${Number(r.offer_psf).toFixed(2)}/SF
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${CARD} border-[rgba(79,157,118,0.22)]`}>
        <div className="px-4 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-[#f4f4f4]">Has the owner engaged?</h3>
            <p className="text-[11.5px] text-[#8a8a8a] mt-1 leading-relaxed">
              Promoting this makes it a deal: it gets a folder, and the next
              underwriting pass extracts, normalises, underwrites a V1 and loads
              it into Proxy. Until then there is nothing to put on a deal page,
              which is why this record does not pretend otherwise.
            </p>
          </div>
          <button
            disabled={readOnly}
            onClick={() => onPromote?.(deal)}
            className="px-4 py-2 rounded-lg bg-[rgba(79,157,118,0.12)] text-[#7cbb99] ring-1 ring-[rgba(79,157,118,0.35)] text-[12.5px] font-medium disabled:opacity-40 shrink-0">
            Promote to deal
          </button>
        </div>
      </div>
    </div>
  );
}
